import { useCallback, useEffect, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { InputText } from 'app/components/elements/InputText';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { createTriggerSubType, getTriggerSubTypes, updateTriggerSubType } from 'services/asset';
import { useFetchTriggerSubTypes } from 'store/triggerSubType';
import { useFlash } from 'contexts/flash';
import { TriggerSubType } from '../../../type';

// Embedded inside the Runtime trigger type's edit modal — lets an admin add new
// sub types and rename existing ones. No delete: the backend has no DELETE route
// for trigger sub types (intentional — sub types may already be referenced by
// saved maintenance schedules).
export const TriggerSubTypeSection = () => {
  const { setFlash } = useFlash();
  const refreshSubTypeStore = useFetchTriggerSubTypes();

  const [subTypes, setSubTypes] = useState<TriggerSubType[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingUnit, setEditingUnit] = useState('');

  const loadSubTypes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTriggerSubTypes();
      setSubTypes(data.triggerSubTypes || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubTypes();
  }, [loadSubTypes]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    const defaultUnit = newUnit.trim();
    await createTriggerSubType({ name, ...(defaultUnit ? { defaultUnit } : {}) });
    setFlash({ type: 'success', message: 'Sub type created successfully' });
    setNewName('');
    setNewUnit('');
    await Promise.all([loadSubTypes(), refreshSubTypeStore()]);
  };

  const startEditing = (subType: TriggerSubType) => {
    setEditingId(subType.id);
    setEditingName(subType.name);
    setEditingUnit(subType.defaultUnit ?? '');
  };

  const handleSaveEdit = async (id: number) => {
    const name = editingName.trim();
    if (!name) return;
    await updateTriggerSubType(id, { name, defaultUnit: editingUnit.trim() });
    setFlash({ type: 'success', message: 'Sub type updated successfully' });
    setEditingId(null);
    await Promise.all([loadSubTypes(), refreshSubTypeStore()]);
  };

  return (
    <Stack gap={1.5} sx={{ border: '1px solid #D0D9E4', borderRadius: '10px', p: 2 }}>
      <Typography fontWeight={700} color={'#226AAA'}>
        Sub Types
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Shown in the Runtime trigger&apos;s Sub Type dropdown on Asset Models. Sub types can be
        added, renamed, and have their default unit set here, but not deleted.
      </Typography>

      <Stack gap={1}>
        {subTypes.map((subType) => (
          <Stack key={subType.id} flexDirection="row" gap={1} alignItems="center">
            {editingId === subType.id ? (
              <>
                <InputText
                  placeholder="Sub type name"
                  inputProps={{
                    value: editingName,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      setEditingName(e.target?.value ?? '')
                  }}
                />
                <InputText
                  placeholder="Default unit"
                  inputProps={{
                    value: editingUnit,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      setEditingUnit(e.target?.value ?? '')
                  }}
                />
                <IconButton onClick={() => handleSaveEdit(subType.id)}>
                  <CheckIcon />
                </IconButton>
                <IconButton onClick={() => setEditingId(null)}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography sx={{ flex: 1 }}>
                  {subType.name}
                  {subType.defaultUnit ? ` (${subType.defaultUnit})` : ''}
                </Typography>
                <IconButton onClick={() => startEditing(subType)}>
                  <EditIcon />
                </IconButton>
              </>
            )}
          </Stack>
        ))}
        {!loading && subTypes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No sub types yet.
          </Typography>
        )}
      </Stack>

      <Stack flexDirection="row" gap={1} alignItems="center">
        <InputText
          placeholder="New sub type name"
          inputProps={{
            value: newName,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target?.value ?? '')
          }}
        />
        <InputText
          placeholder="Default unit"
          inputProps={{
            value: newUnit,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNewUnit(e.target?.value ?? '')
          }}
        />
        <ButtonCustom
          label="Add"
          style={{ width: 'fit-content' }}
          onClick={handleAdd}
          disabled={!newName.trim() || loading}
        />
      </Stack>
    </Stack>
  );
};
